import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Crown, Flame, Trophy, Dumbbell, Loader2 } from "lucide-react";
import TopGifterCard from "@/components/TopGifterCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = [
  { key: "signin_streak", label: "Sign-In", icon: Flame, suffix: "d" },
  { key: "activity_streak", label: "Activity", icon: Flame, suffix: "d" },
  { key: "badges", label: "Badges", icon: Trophy, suffix: "" },
  { key: "workouts", label: "Workouts", icon: Dumbbell, suffix: "" },
];

const MEDAL = {
  1: "bg-[#D4AF37] text-black",
  2: "bg-neutral-300 text-black",
  3: "bg-amber-700 text-black",
};

export default function HallOfFame() {
  const [sort, setSort] = useState("signin_streak");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hall-of-fame?sort=${sort}&limit=10`);
      setRows(data.rows || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => {
    load();
  }, [load]);

  const valueFor = (row) => {
    if (sort === "signin_streak") return row.signin_streak;
    if (sort === "activity_streak") return row.activity_streak;
    if (sort === "workouts") return row.workouts_count;
    return row.badges_count;
  };

  const activeSort = SORTS.find((s) => s.key === sort) || SORTS[0];

  return (
    <section
      id="hall-of-fame"
      data-testid="hall-of-fame"
      className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] bg-[#D4AF37] rounded-full blur-[160px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold mb-2 inline-flex items-center gap-2">
            <Crown size={12} /> Hall of Fame
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
            The Top of <span className="text-[#D4AF37]">The Circle.</span>
          </h2>
          <p className="text-sm md:text-base text-neutral-400 max-w-md mx-auto mt-3">
            Real athletes. Real consistency. Real proof that holistic healing
            compounds.
          </p>
        </div>

        {/* Sort tabs */}
        <div className="flex justify-center flex-wrap gap-1.5 mb-8">
          {SORTS.map((s) => {
            const Icon = s.icon;
            const on = s.key === sort;
            return (
              <button
                key={s.key}
                data-testid={`hof-sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 inline-flex items-center gap-1.5 border-2 transition-colors ${
                  on
                    ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                    : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                }`}
              >
                <Icon size={11} /> {s.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-6 text-sm">
            <Loader2 size={20} className="animate-spin inline-block mr-2" />
            Loading the champions…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center text-neutral-500 py-6 text-sm max-w-md mx-auto">
            No champions yet on this leaderboard. Start your streak and you could be the first 🐦‍🔥
          </div>
        ) : (
          <ol className="space-y-2 max-w-2xl mx-auto">
            {rows.map((r) => (
              <li
                key={r.user_id}
                data-testid={`hof-row-${r.rank}`}
                className="flex items-center gap-3 p-3 md:p-4 bg-[#0F0F0F] border border-zinc-900 hover:border-[#D4AF37] transition-colors"
              >
                <span
                  className={`flex-shrink-0 w-10 h-10 flex items-center justify-center font-display text-xl ${
                    MEDAL[r.rank] || "text-neutral-500 border border-zinc-700"
                  }`}
                >
                  {r.rank}
                </span>
                {r.picture ? (
                  <img
                    src={r.picture}
                    alt={r.name}
                    className="w-12 h-12 object-contain bg-black border-2 border-[#D4AF37]"
                  />
                ) : (
                  <div className="w-12 h-12 bg-[#4C1D95] border-2 border-[#D4AF37] flex items-center justify-center font-display text-lg text-[#D4AF37]">
                    {(r.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-display text-lg text-white truncate">
                      {r.name}
                    </span>
                    {r.role === "owner" && (
                      <span
                        className="bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-0.5"
                        title="Founder"
                      >
                        <Crown size={9} strokeWidth={2.5} />
                      </span>
                    )}
                    {r.is_premium && r.role !== "owner" && (
                      <span className="bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-1.5 py-0.5">
                        ★
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Flame size={10} className="text-[#DC2626]" /> {r.signin_streak}d
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Trophy size={10} className="text-[#D4AF37]" /> {r.badges_count}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 inline-flex items-center gap-1.5 text-[#D4AF37] font-bold">
                  <activeSort.icon size={16} />
                  <span className="font-mono tabular-nums text-xl">
                    {valueFor(r)}
                    {activeSort.suffix}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}

        <p className="text-[10px] text-neutral-600 mt-8 text-center uppercase tracking-widest">
          Want in? <a href="#ai-coach" className="text-[#D4AF37] hover:underline font-bold">Get your plan</a> · Start your streak · Climb the ranks.
        </p>

        {/* Top Gifter This Week — separate leaderboard for generosity */}
        <div className="mt-12 max-w-2xl mx-auto">
          <TopGifterCard />
        </div>
      </div>
    </section>
  );
}
